/**
 * Request a pre-signed R2 download URL from the server
 * after verifying the user has purchased the product.
 */
import { getSupabaseAccessToken } from './getSupabaseAccessToken';

export async function createR2DownloadUrl(
  serverUrl: string,
  userId: string,
  productId: string,
  fileName: string
): Promise<string> {
  const base = serverUrl.replace(/\/+$/, '');
  const token = await getSupabaseAccessToken();

  let res: Response;
  try {
    res = await fetch(`${base}/r2/download-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId, productId, fileName }),
    });
  } catch (e) {
    const isNetwork =
      e instanceof TypeError &&
      (e.message === 'Failed to fetch' || e.message?.toLowerCase().includes('network'));
    if (isNetwork) {
      throw new Error(
        'Could not reach the download server. On a phone, this is often a www vs non-www mismatch: ' +
          'set FRONTEND_URL on the API server to match the site URL you use (or add CORS_EXTRA_ORIGINS). ' +
          'Also ensure REACT_APP_SERVER_URL uses https in production.'
      );
    }
    throw e instanceof Error ? e : new Error('Download request failed');
  }

  if (!res.ok) {
    let message = `Failed to create download URL (status ${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) {
        message = body.error;
      }
    } catch {
      // ignore JSON parse errors, keep default message
    }
    throw new Error(message);
  }

  const { url } = (await res.json()) as { url: string };
  if (!url) {
    throw new Error('Server did not return a download URL');
  }

  return url;
}

